import {
  FeatureFlag,
  type FeatureFlagType,
  PermissionSlug,
} from './featureFlags';

/**
 * One entry per section of the Admin tab.
 *
 * The tab itself has no permission of its own: it appears when the user can see
 * at least one section, which is what {@link visibleAdminSections} answers. A single
 * master slug meant a granular permission was useless without it, so granting
 * one and forgetting the other silently hid the section.
 */
export interface AdminSectionDefinition {
  /** Stable id, used as the tab value and the `?section=` query parameter. */
  id: string;
  label: string;
  viewSlug: string;
  editSlug: string;
  /** Sections that only exist where the EMR flows do. */
  rogerEmrOnly: boolean;
  /**
   * A WorkOS org flag the section is hidden behind while it is in progress.
   * Omitted for a section that is simply live.
   */
  featureFlag?: FeatureFlagType;
}

export const ADMIN_SECTIONS: readonly AdminSectionDefinition[] = [
  {
    id: 'clinic-info',
    label: 'Clinics',
    viewSlug: PermissionSlug.CLINIC_INFO_VIEW,
    editSlug: PermissionSlug.CLINIC_INFO_EDIT,
    rogerEmrOnly: false,
  },
  {
    id: 'insurance',
    label: 'Insurance',
    viewSlug: PermissionSlug.AGENCY_INSURANCE_VIEW,
    editSlug: PermissionSlug.AGENCY_INSURANCE_EDIT,
    rogerEmrOnly: false,
  },
  {
    id: 'enrollments',
    label: 'Enrollments',
    viewSlug: PermissionSlug.AGENCY_INSURANCE_VIEW,
    editSlug: PermissionSlug.AGENCY_INSURANCE_EDIT,
    rogerEmrOnly: true,
    featureFlag: FeatureFlag.ADMIN_TAB_ENROLLMENTS,
  },
  {
    id: 'physicians',
    label: 'Physicians',
    viewSlug: PermissionSlug.CLINIC_PHYSICIANS_VIEW,
    editSlug: PermissionSlug.CLINIC_PHYSICIANS_EDIT,
    rogerEmrOnly: false,
  },
  {
    id: 'referral-sources',
    label: 'Referral Sources',
    viewSlug: PermissionSlug.REFERRAL_SOURCES_VIEW,
    editSlug: PermissionSlug.REFERRAL_SOURCES_EDIT,
    rogerEmrOnly: true,
  },
  {
    id: 'chart-workflow',
    label: 'Chart Workflow',
    viewSlug: PermissionSlug.CHART_WORKFLOW_VIEW,
    editSlug: PermissionSlug.CHART_WORKFLOW_EDIT,
    rogerEmrOnly: true,
  },
  {
    id: 'patient-creation',
    label: 'Patient Creation',
    viewSlug: PermissionSlug.PATIENT_CREATION_SETTINGS_VIEW,
    editSlug: PermissionSlug.PATIENT_CREATION_SETTINGS_EDIT,
    rogerEmrOnly: true,
  },
  {
    // Staff credential compliance is not an EMR-only concern, and User Manager
    // is shared with intake-only agencies, so this one is not rogerEmrOnly.
    // The catalog has no separate view slug: the screen is the edit screen.
    id: 'user-license-types',
    label: 'License Types',
    viewSlug: PermissionSlug.USER_LICENSE_TYPES_EDIT,
    editSlug: PermissionSlug.USER_LICENSE_TYPES_EDIT,
    rogerEmrOnly: false,
  },
  {
    id: 'scheduling-points',
    label: 'Scheduling Points',
    viewSlug: PermissionSlug.SCHEDULING_POINTS_VIEW_CONFIG,
    editSlug: PermissionSlug.SCHEDULING_POINTS_EDIT_CONFIG,
    rogerEmrOnly: true,
  },
  {
    id: 'hhrg-costs',
    label: 'HHRG Costs',
    viewSlug: PermissionSlug.HHRG_COSTS_VIEW,
    editSlug: PermissionSlug.HHRG_COSTS_EDIT,
    rogerEmrOnly: true,
  },
  {
    // Which task types are covered care and which are paperwork. EMR-only:
    // the rows are visit types, which an intake-only agency never schedules.
    id: 'task-types',
    label: 'Task Types',
    viewSlug: PermissionSlug.TASK_TYPES_VIEW,
    editSlug: PermissionSlug.TASK_TYPES_EDIT,
    rogerEmrOnly: true,
  },
  {
    // The library of standardized text an agency loads into visit-form text
    // boxes. EMR-only because the fields it targets are visit form questions,
    // which an intake-only agency has none of.
    id: 'form-templates',
    label: 'Text Templates',
    viewSlug: PermissionSlug.FORM_TEMPLATES_VIEW,
    editSlug: PermissionSlug.FORM_TEMPLATES_EDIT,
    rogerEmrOnly: true,
  },
];

/**
 * The sections this user actually gets, in tab order.
 *
 * The one answer to "is there an Admin tab for this person" — the sidebar link,
 * the page's own gate and the tab strip all call this so they cannot disagree.
 * They did once: the gate counted `rogerEmrOnly` sections that the tab strip
 * then filtered out, so a user at an agency without the EMR product who held
 * only EMR-only slugs was let in to a page with no tabs on it.
 */
export function visibleAdminSections(
  hasAnyPermission: (slugs: string[]) => boolean,
  isRogerEmrEnabled: boolean,
  hasFeatureFlag: (featureFlag: FeatureFlagType) => boolean
): AdminSectionDefinition[] {
  return ADMIN_SECTIONS.filter(section => {
    if (section.rogerEmrOnly && !isRogerEmrEnabled) return false;
    // Required, not optional with a default: the sidebar link, the landing-path
    // gate and the tab strip all call this, and a caller that silently skipped
    // the flag would reopen the disagreement described above.
    if (section.featureFlag && !hasFeatureFlag(section.featureFlag))
      return false;
    return hasAnyPermission([section.viewSlug, section.editSlug]);
  });
}
